import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { withSize } from 'react-sizeme';
import { makeStyles } from '@material-ui/core/styles';
import { Colors, Metrics } from '../../themes';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import clsx from 'clsx';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Select from '../common/Select';
import Button from '@material-ui/core/Button';
import SvgIcon from '../../icons/SvgIcon';
import Table from '../common/Table';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Checkbox from '@material-ui/core/Checkbox';
import IconButton from '@material-ui/core/IconButton';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import isAfter from 'date-fns/isAfter';
import { hackToSubtractOffset } from '../../lib/Utils';
// include all the lines below for date picker
import DatePicker from 'react-datepicker';
import i18n from '../../i18n';
import '../common/styles/react-datepicker.custom.css';
import { registerLocale } from 'react-datepicker';
import fi from 'date-fns/locale/fi';
import pl from 'date-fns/locale/pl';
import de from 'date-fns/locale/de';
import sv from 'date-fns/locale/sv';
import es from 'date-fns/locale/es';
registerLocale('fi', fi);
registerLocale('pl', pl);
registerLocale('de', de);
registerLocale('sv', sv);
registerLocale('es', es);
// end of date picker

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`wrapped-tabpanel-${index}`}
      aria-labelledby={`wrapped-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `wrapped-tab-${index}`,
    'aria-controls': `wrapped-tabpanel-${index}`,
  };
}

const useStyles = makeStyles((theme) => {
  return {
    DetailsWidget: {
      display: 'flex',
      flexDirection: 'column',
      overflowWrap: 'break-word',
    },
    headerButtonsContainer: {
      background: Colors.navBackgroundGrey,
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      borderRadius: '4px 4px 0 0',
      padding: 10,
      [theme.breakpoints.down(Metrics.minWidth)]: {
        padding: 6,
        justifyContent: 'flex-start',
      },
    },
    button: {
      margin: theme.spacing(2),
      fontSize: 14,
      color: Colors.white,
      border: `1px solid ${Colors.disabledGrey}`,
      '&:hover': {
        background: Colors.disabledGrey,
      },
      '&:disabled': {
        border: `1px solid ${Colors.disabledGrey}`,
        background: Colors.navBackgroundGrey,
        '&:hover': {
          background: Colors.black,
        },
      },
    },
    smallSizeButton: {
      height: 35,
      fontSize: 12,
      paddingLeft: 6,
      paddingRight: 6,
    },
    saveButton: {
      background: Colors.blue,
      border: 'none',
      '&:hover': {
        background: Colors.lightBlue,
      },
    },
    deleteButton: {
      margin: 10,
      marginRight: 'auto',
      minWidth: 38,
      width: 38,
      maxWidth: 38,
      padding: 0,
      borderColor: Colors.red,
      '&:hover': {
        background: Colors.red,
      },
      [theme.breakpoints.down(Metrics.minWidth)]: {
        marginRight: 8,
      },
      '&:disabled:hover': {
        background: Colors.black,
      },
    },
    // details
    detailsContainer: {
      padding: '25px 20px',
      [theme.breakpoints.down('xs')]: {
        padding: 15,
      },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    formContainer: {
      alignSelf: 'center',
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
    },
    errorDisplay: {
      margin: '20px 10px 0 10px',
      color: Colors.errorRed,
    },
    filterTitleRow: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-start',
      alignItems: 'center',
    },
    header2: {
      cursor: 'pointer',
    },
    executeButton: {
      marginLeft: 'auto',
      marginRight: 0,
    },
    filterBox: {
      marginTop: 40,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    tab: (containerWidth) => {
      let tabWidth;
      if (containerWidth > 520) {
        tabWidth = 120;
      } else if (containerWidth > 365) {
        tabWidth = 82;
      } else {
        tabWidth = 78;
      }
      return {
        minWidth: tabWidth,
        width: tabWidth,
      };
    },
    checkAll: {
      textAlign: 'left',
    },
    checkAllLabel: {
      marginLeft: 20,
      fontSize: 16,
    },
  };
});

const EventReportDetails = (props) => {
  const { t } = useTranslation();
  const { width } = props.size;
  const classes = useStyles(width);
  const isSmall = width < Metrics.formDetails.small;
  const isOneColumn = useMediaQuery(`(max-width:${Metrics.oneColumn}px)`);
  const fullWidth = width - 40;
  const formInputWidth = isSmall ? fullWidth : (width - 65) / 2;
  const {
    deleteButtonText,
    add1ButtonText,
    onDeleteButtonClick,
    onAdd1ButtonClick,
    add2ButtonText,
    onAdd2ButtonClick,
    onSaveButtonClick,
    isDeleteButtonDisabled,
    onChange,
    hideDetails,
    name,
    isUntouched,
    setUntouched,
  } = props;
  const smallerThanMedium = useMediaQuery(`(max-width: ${Metrics.breakpoints.md}px)`);
  const handleSubmit = (event) => {
    event.preventDefault();
    onSaveButtonClick();
  };
  const [isNameError, setNameError] = useState(false);
  const [nameHelperText, setNameHelperText] = useState(null);
  const [isNameTouched, setNameTouched] = useState(false);
  const [isFormValid, setFormValid] = useState(false);

  const setFieldsTouched = useCallback(
    (value) => {
      setNameTouched(value);
      // setSchedulesTouched(value);
      setUntouched(false);
    },
    [setUntouched]
  );

  useEffect(() => {
    const validate = () => {
      const isNameValid = name !== '';
      if (!isNameValid && !isNameTouched) {
        setFormValid(false);
      }
      if (!isNameValid && isNameTouched) {
        setNameError(true);
        setNameHelperText(t('validation.requiredField'));
        setFormValid(false);
      }
      if (isNameValid) {
        setNameError(false);
        setNameHelperText(null);
        setFormValid(true);
      }
    };
    validate();
  }, [name, isNameTouched, t]);

  useEffect(() => {
    if (isUntouched) {
      setFieldsTouched(false);
      setNameError(false);
      setNameHelperText(null);
    }
  }, [isUntouched, setFieldsTouched]);

  const handleBlur = (event) => {
    const { name } = event.target;
    if (name === 'name') {
      setNameTouched(true);
    }
    if (name === 'name') {
      setUntouched(false);
    }
  };

  // validation before executing report
  const [reportHelperText, setReportHelperText] = useState(null);
  const [isReportValid, setReportValid] = useState(false);

  const validateReport = () => {
    const isEndTimeNotNull = Boolean(props.endTime);
    const isEndTimeAfterStartTime = isAfter(new Date(props.endTime), new Date(props.startTime));
    const errorText = !isEndTimeNotNull
      ? t('reportManager_error_noEndDate')
      : !isEndTimeAfterStartTime
      ? t('scheduleManager_error_end_before_start')
      : null;
    setReportValid(isEndTimeNotNull && isEndTimeAfterStartTime);
    setReportHelperText(errorText);
  };
  useEffect(validateReport, [props.startTime, props.endTime]);
  const errorDisplay = props.isExecuteButtonHovered && reportHelperText && (
    <div className={classes.errorDisplay}>{reportHelperText}</div>
  );
  // filters
  const [isFiltersVisible, setFiltersVisible] = useState(false);
  // tabs
  const [tabValue, setTabValue] = React.useState('sites');
  useEffect(() => setTabValue('sites'), [props.reportModelId]);
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // checkboxes

  const handleCheckbox = (value, field) => {
    const checkedField = `checked${field.charAt(0).toUpperCase()}${field.slice(1)}s`;
    const fieldId = `${field}Id`;
    const previousChecked = props[checkedField];
    //check if the site was present in checkedSites
    const selectedIndex = previousChecked.findIndex((item) => item[fieldId] === value[fieldId]);
    let newChecked = [];
    if (selectedIndex === -1) {
      // if not found, add it
      newChecked = newChecked.concat(previousChecked, value);
    } else if (selectedIndex === 0) {
      // if first item, remove it
      newChecked = newChecked.concat(previousChecked.slice(1));
    } else if (selectedIndex === previousChecked.length - 1) {
      // if last item, remove it
      newChecked = newChecked.concat(previousChecked.slice(0, -1));
    } else if (selectedIndex > 0) {
      // if between first and last, remove it
      newChecked = newChecked.concat(previousChecked.slice(0, selectedIndex), previousChecked.slice(selectedIndex + 1));
    }
    props.changeCheckedValues(newChecked, checkedField);
  };

  return (
    <div className={classes.DetailsWidget}>
      <form id="detailsForm" noValidate autoComplete="off" onSubmit={handleSubmit}>
        <div className={classes.headerButtonsContainer}>
          <Button
            type="button"
            className={clsx(classes.deleteButton, isSmall && classes.smallSizeButton)}
            variant="outlined"
            aria-label={deleteButtonText}
            onClick={onDeleteButtonClick}
            disabled={isDeleteButtonDisabled}
          >
            <SvgIcon
              icon={'Trash'}
              color={isDeleteButtonDisabled ? Colors.disabledGrey : Colors.white}
              stroke={2}
              width={14}
            />
          </Button>
          <Button
            type="button"
            variant="outlined"
            className={clsx(classes.button, isSmall && classes.smallSizeButton)}
            onClick={onAdd1ButtonClick}
            endIcon={<SvgIcon icon="Add" width={smallerThanMedium ? 16 : 20} color={Colors.white} />}
          >
            {add1ButtonText}{' '}
          </Button>
          <Button
            type="submit"
            variant="contained"
            className={clsx(classes.button, classes.saveButton, isSmall && classes.smallSizeButton)}
            endIcon={
              <SvgIcon
                icon="Save"
                width={smallerThanMedium ? 16 : 20}
                color={!isFormValid ? Colors.disabledGrey : Colors.white}
              />
            }
            disabled={!isFormValid}
            onMouseEnter={() => setFieldsTouched(true)}
          >
            {t('Global_save')}
          </Button>
        </div>
        {!hideDetails && (
          <div className="bodyComponent">
            <div className={classes.detailsContainer}>
              <Typography variant="h2">{t('reportManager_modelInfo_header').toUpperCase()}</Typography>
              <div className={classes.formContainer}>
                <TextField
                  style={{ width: formInputWidth }}
                  id="name"
                  autoComplete="off"
                  error={isNameError}
                  helperText={nameHelperText}
                  label={t('reportManager_modelInfo_name')}
                  name="name"
                  margin="normal"
                  value={props.name}
                  onChange={onChange}
                  onBlur={handleBlur}
                  required
                />
                <TextField
                  style={{ width: formInputWidth }}
                  id="description"
                  label={t('reportManager_modelInfo_description')}
                  name="description"
                  margin="normal"
                  value={props.description}
                  onChange={onChange}
                />
                <Select
                  singleElement
                  selectWidth={formInputWidth}
                  selectData={props.datePresetSelectData}
                  selectLabel={props.datePresetSelectLabel}
                  selectValue={props.datePresetSelectValue}
                  onSelect={props.datePresetOnSelect}
                  accessValue={props.datePresetAccessValue}
                  accessLabel={props.datePresetAccessLabel}
                  error={!isReportValid && props.isExecuteButtonHovered}
                  {...props}
                />
                {/* startTime */}
                <DatePicker
                  showPopperArrow={false}
                  selected={
                    props.startTime
                      ? new Date(hackToSubtractOffset(props.startTime))
                      : new Date(hackToSubtractOffset(new Date().toJSON()))
                  }
                  onChange={props.handleStartDateChange}
                  locale={i18n.language}
                  dateFormat="d.M.yyyy H:mm"
                  timeFormat="H:mm"
                  timeIntervals={1}
                  shouldCloseOnSelect={false}
                  timeInputLabel={`${t('common.time')}:`}
                  showTimeInput={true}
                  {...(isOneColumn && {
                    popperModifiers: {
                      offset: {
                        enabled: true,
                        offset: '15px, 10px',
                      },
                      preventOverflow: {
                        enabled: true,
                        escapeWithReference: false,
                        boundariesElement: 'viewport',
                      },
                    },
                  })}
                  customInput={
                    <TextField
                      className={classes.textField}
                      style={{ width: formInputWidth }}
                      label={t('persons.from')}
                      error={!isReportValid && props.isExecuteButtonHovered}
                      required
                    />
                  }
                />
                {/* endTime */}
                <DatePicker
                  showPopperArrow={false}
                  selected={props.endTime ? new Date(hackToSubtractOffset(props.endTime)) : null}
                  {...(props.startTime && {
                    minDate: new Date(hackToSubtractOffset(props.startTime)),
                  })}
                  onChange={props.handleEndDateChange}
                  locale={i18n.language}
                  dateFormat="d.M.yyyy H:mm"
                  timeFormat="H:mm"
                  timeIntervals={1}
                  shouldCloseOnSelect={false}
                  timeInputLabel={`${t('common.time')}:`}
                  showTimeInput={true}
                  popperModifiers={{
                    offset: {
                      enabled: true,
                      offset: isOneColumn ? '15px, 10px' : '-20px, 10px',
                    },
                    preventOverflow: {
                      enabled: true,
                      escapeWithReference: false,
                      boundariesElement: 'viewport',
                    },
                  }}
                  customInput={
                    <TextField
                      className={classes.textField}
                      style={{ width: formInputWidth }}
                      label={t('persons.to')}
                      error={!isReportValid && props.isExecuteButtonHovered}
                      required
                    />
                  }
                />
              </div>
              {errorDisplay}
              <div className={classes.filterBox}>
                <div className={classes.filterTitleRow} style={{ width: fullWidth }}>
                  <Typography
                    className={classes.header2}
                    variant="h2"
                    onClick={() => setFiltersVisible(!isFiltersVisible)}
                  >
                    {t('reportManager_filters').toUpperCase()}
                  </Typography>
                  <IconButton
                    onClick={() => setFiltersVisible(!isFiltersVisible)}
                    className={classes.toggleButton}
                    key={'reports-toggle'}
                  >
                    {isFiltersVisible ? (
                      <ExpandLessIcon className={clsx([classes.icon])} />
                    ) : (
                      <ExpandMoreIcon className={clsx([classes.icon])} />
                    )}
                  </IconButton>
                  <Button
                    type="button"
                    variant="outlined"
                    className={clsx(classes.button, isSmall && classes.smallSizeButton, classes.executeButton)}
                    onClick={onAdd2ButtonClick}
                    endIcon={<AutorenewIcon />}
                    disabled={!isReportValid}
                    onMouseEnter={() => props.setExecuteButtonHovered(true)}
                  >
                    {add2ButtonText}
                  </Button>
                </div>
                {isFiltersVisible && (
                  <Tabs value={tabValue} onChange={handleTabChange} aria-label="filter tabs">
                    <Tab
                      classes={{ root: classes.tab }}
                      value="sites"
                      label={t('reportManager_SitesTabName')}
                      wrapped
                      {...a11yProps('sites')}
                    />
                    <Tab
                      classes={{ root: classes.tab }}
                      value="doors"
                      label={t('reportManager_ControllerTabName')}
                      {...a11yProps('doors')}
                    />
                    <Tab
                      classes={{ root: classes.tab }}
                      value="persons"
                      label={t('reportManager_PersonsTabName')}
                      {...a11yProps('persons')}
                    />
                    <Tab
                      classes={{ root: classes.tab }}
                      value="columns"
                      label={t('reportManager_ColumnsTabName')}
                      {...a11yProps('columns')}
                    />
                  </Tabs>
                )}
              </div>
            </div>
          </div>
        )}
      </form>
      {!hideDetails && isFiltersVisible && (
        <div className={classes.filtersTableContainer}>
          {/* sites panel */}
          <TabPanel className={classes.weeklyTab} value={tabValue} index="sites">
            <div className={classes.checkAll}>
              <Checkbox
                checked={props.isSelectAllSitesButtonChecked}
                inputProps={{ 'aria-labelledby': 'check-all-sites' }}
                onChange={props.changeSelectAllSitesButton}
              />
              <span className={classes.checkAllLabel}>{t('Global_selectAll')}</span>
            </div>
            <Table
              data={props.sites}
              columns={[{ title: t('reportManager_SitesFilterTable_col1'), field: 'name' }]}
              noDataText={t('controllerManager_noSite')}
              checkboxColumn="start"
              checkedArray={props.checkedSites}
              handleClick={(site) => handleCheckbox(site, 'site')}
              activeRowField={'siteId'}
              noHeader
              noFooter
            />
          </TabPanel>
          {/* doors table */}
          <TabPanel className={classes.tabPanel} value={tabValue} index="doors">
            <div className={classes.checkAll}>
              <Checkbox
                checked={props.isSelectAllControllersButtonChecked}
                inputProps={{ 'aria-labelledby': 'check-all-controllers' }}
                onChange={props.changeSelectAllControllersButton}
              />
              <span className={classes.checkAllLabel}>{t('Global_selectAll')}</span>
            </div>
            <Table
              data={props.controllers}
              columns={[
                { title: t('reportManager_ControllersFilterTab_col1'), field: 'siteName' },
                { title: t('reportManager_ControllersFilterTab_col2'), field: 'controllerName' },
              ]}
              noDataText={t('sites.noDoor')}
              checkboxColumn="start"
              checkedArray={props.checkedControllers}
              handleClick={(controller) => handleCheckbox(controller, 'controller')}
              activeRowField={'controllerId'}
              noHeader
              noFooter
            />
          </TabPanel>
          {/* persons table */}
          <TabPanel className={classes.tabPanel} value={tabValue} index="persons">
            <div className={classes.checkAll}>
              <Checkbox
                checked={props.isSelectAllPersonsButtonChecked}
                inputProps={{ 'aria-labelledby': 'check-all-persons' }}
                onChange={props.changeSelectAllPersonsButton}
              />
              <span className={classes.checkAllLabel}>{t('Global_selectAll')}</span>
            </div>
            <Table
              data={props.persons}
              columns={[{ title: t('reportManager_PersonsFilterTable_col1'), field: 'name' }]}
              noDataText={t('persons.noPersons')}
              checkboxColumn="start"
              checkedArray={props.checkedPersons}
              handleClick={(person) => handleCheckbox(person, 'person')}
              activeRowField={'personId'}
              noHeader
              noFooter
            />
          </TabPanel>
          {/* columns table */}
          <TabPanel className={classes.tabPanel} value={tabValue} index="columns">
            <div className={classes.checkAll}>
              <Checkbox
                checked={props.isSelectAllColumnsButtonChecked}
                inputProps={{ 'aria-labelledby': 'check-all-columns' }}
                onChange={props.changeSelectAllColumnsButton}
              />
              <span className={classes.checkAllLabel}>{t('reportManager_Columns_default')}</span>
            </div>
            <Table
              data={props.columns}
              columns={[{ title: t('reportManager_ColumnsFilterTable_col1'), field: 'translation' }]}
              checkboxColumn="start"
              checkedArray={props.checkedColumns}
              handleClick={(column) => handleCheckbox(column, 'column')}
              activeRowField={'columnId'}
              noHeader
              noFooter
            />
          </TabPanel>
        </div>
      )}
    </div>
  );
};

export default withSize()(EventReportDetails);
